import { Dialog, DialogContent } from '@material-ui/core';
import { ProductType, isKorpoRegisterMode, PRODUCTS } from 'cart-model';
import { useRouter } from 'common-react';
import { AnimatePresence, motion } from 'framer-motion';
import { FlamingoFund } from 'funds-model';
import { ToISODateOptions } from 'luxon';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  Card,
  useIsMobile,
  FundBigChart, ChartType, ROI, Typography, FundDetailsDialogBottomBar,
  FundDetailsDialogActionTopBar,
  FundDetailsDialogGrid,
  FundDetailsDialogHeader,
  HTMLContent,
  FundListItemIcons,
  toHashColorHexString,
  Button,
  Header,
  HeaderType,
  zIndex,
} from 'ui-components';
import { EventType } from 'common-event';
import { logFundEvent } from '../../model/events';
import { useFundEventLoggerWithContext } from '../../model/FundEventsContext';
import ProductWizardState from '../../model/ProductWizardState';
import Screen from '../ProductWizard/Screen';
import { getFundsChartData, getFundsChartLastDate } from './chartsApi';
import { getFundManagerPhoto } from './fundDetailsDialogApi';
import { useFundDetailsDialogPhoto } from './useFundDetailsDialogPhoto';

import useStyles from './FundDetailsButtonWithDialog.style';

export interface FundDetailsButtonWithDialogProps {
  fund: FlamingoFund,
  productType: ProductType,
  buttonLabel: string,
  className?: string,
  source: Screen,
  index: number,
  riskProfilerLevel?: number,
  changeRateKey?: ROI;
  onSelectFund?: (fund: FlamingoFund, setIsButtonLoading?: (value: boolean) => void) => void;
  customButton?: React.ReactNode;
  titleAsButton?: boolean;
  isFundSelected?: boolean;
}

const FundDetailsButtonWithDialog:FunctionComponent<FundDetailsButtonWithDialogProps> = ({ fund, productType, buttonLabel, className, source, index, riskProfilerLevel, changeRateKey, onSelectFund, customButton, titleAsButton, isFundSelected }) => {

  const titleLength = fund.name.length + (fund.previousName?.length || 0);
  const c = useStyles({ titleLength });

  const [t] = useTranslation('components');
  const isMobile = useIsMobile();
  const { history } = useRouter();

  const [open, setOpen] = useState(false);
  const [openPopupTimestamp, setOpenPopupTimestamp] = useState<Date | undefined>(undefined);
  const getOpenPopupInterval = () : number | undefined => openPopupTimestamp ? new Date().getTime() - openPopupTimestamp.getTime() : undefined;
  const [isActionTopBarVisible, setIsActionTopBarVisible] = useState<boolean | undefined>(true);
  const { logFundSelection } = useFundEventLoggerWithContext();

  const korpoData = useSelector((state: ProductWizardState) => state.cartState.korpoData);
  const registerMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const isKorpo = isKorpoRegisterMode(registerMode);
  const unitCategoryCode = isKorpo && korpoData != null ? korpoData.unitCategoryCode : PRODUCTS[productType].unitCategory;
  const unitCategory = useSelector((state: ProductWizardState) => state.apiState.unitCategories)
      .find(unit => unit.code === unitCategoryCode);
  const showBottomBar = !isActionTopBarVisible &&  !!onSelectFund;
  const [isButtonVisible, setIsButtonVisible] = useState<boolean | undefined>(false);
  const [isButtonHovered, setIsButtonHovered] = useState<boolean | undefined>(false);

  const openHandler = () => {
    setOpen(true);
    logFundEvent(EventType.productViewPopup, source, fund, productType);
    logFundSelection(fund.id, productType, index, riskProfilerLevel);
    setOpenPopupTimestamp(new Date());
  };
  const closeHandler = () => {
    setOpen(false);
    logFundEvent(EventType.productClosePopup, source, fund, productType, getOpenPopupInterval());
  };
  const clickHandler = () => {
    closeHandler();
    onSelectFund?.(fund);
  };
  const photo = useFundDetailsDialogPhoto({
    fund,
    isButtonVisible,
    isButtonHovered,
    isMobile
  });
  const getButtonProps = () => ({
    onViewportEnter: ((entry: IntersectionObserverEntry | null) => setIsButtonVisible(entry?.isIntersecting)),
    onViewportLeave: ((entry: IntersectionObserverEntry | null) => setIsButtonVisible(entry?.isIntersecting)),
    onMouseEnter: () => setIsButtonHovered(true),
    onMouseLeave: () => setIsButtonHovered(false)
  });
  const [lastFundDate, setLastFundDate] = useState<string | undefined>(undefined);
  useEffect(() => {
    getFundsChartLastDate(fund.id, unitCategoryCode).then((data) => {
      setLastFundDate(data);
    })
  }, []);

  const renderSelectButton = () => (
    <Button
      className={c.bottomBarButton}
      buttonType={isFundSelected ? 'secondary' : 'primary'}
      buttonVariant="large"
      onClick={clickHandler}
    >
      {isFundSelected ? t('fund-details-dialog.selected') : t('fund-details-dialog.choose')}
    </Button>
  );

  return (
    <>
      {customButton && titleAsButton ? (
        <motion.button
          className={clsx(c.customButton, className, c.titleAsButton)}
          onClick={openHandler}
          {...getButtonProps()}
        >
          {customButton}
        </motion.button>
      ) : (
        <motion.span {...getButtonProps()}>
          <Button
            className={className}
            icon="chevronRight"
            iconPosition="after"
            buttonType="tertiary"
            onClick={openHandler}>
            {buttonLabel}
          </Button>
        </motion.span>
      )}
        <Dialog
          fullWidth
          data-testid="dialog"
          open={open}
          scroll="paper"
          classes={{ paper: c.fundDetailsDialog, scrollPaper: c.fundDetailsDialogPaperWrapper}}
          onClose={closeHandler}
          style={{ zIndex: zIndex.modalZIndex }}
        >
        <DialogContent className={c.dialogContent}>
          <Header
            headerType={HeaderType.CreatorFundDetails}
            renderLogo={(logo, linkClassName) => (
              <NavLink to="/" className={linkClassName}>
                {logo}
              </NavLink>
            )}
          />
          <div className={c.fundDetailsDialogWrapper}>
          <motion.div
            data-testid="dialog-header"
            onViewportLeave={(entry => setIsActionTopBarVisible(entry?.isIntersecting))}
            onViewportEnter={(entry => setIsActionTopBarVisible(entry?.isIntersecting))}
          >
            <FundDetailsDialogHeader
              unitCategoryCode={unitCategoryCode}
              fund={fund}
              closeHandler={closeHandler}
              backgroundImage={fund.fundPhoto ?? photo?.photoBase64}
              backgroundImageType={fund.fundPhotoType ?? photo?.photoContentType}
              processLayout={false}
              hasSidebarNavbar={false}
            />
          </motion.div>
            <Card
            size="equal"
            className={c.fundBigChartCard}
            >
            <FundDetailsDialogActionTopBar
              fund={fund}
              unitCategoryCode={unitCategoryCode}
              showAdditionalTab={!!fund.additionalTab?.content}
              processLayout={false}
              >
                {renderSelectButton()}
              </FundDetailsDialogActionTopBar>
            {fund.additionalTab?.content &&
              <HTMLContent
                className={c.additionalTab}
                content={fund.additionalTab.content}
                onInternalNavigate={history.push}
              />
            }
              <div className={c.fundBigChartWrapper} data-testid="fund-chart">
                <FundBigChart
                    funds={[fund as FlamingoFund]}
                    unitCategoryCode={unitCategoryCode}
                    unitCategory={unitCategory}
                    embeddedInFundPopup
                    changeRateKey={changeRateKey}
                    getFundsChartDataPromise={(fromDate: string, endDate: string | ((options?: ToISODateOptions) => string), unitCategoryCode: string, chartType: ChartType) => {
                      return getFundsChartData([fund],
                          fromDate,
                          endDate,
                          unitCategoryCode,
                          chartType).then(responses => responses.map(response => response.data));
                    }}
                    lastFundDate={lastFundDate}
                />
              </div>
            </Card>
          <FundDetailsDialogGrid
            fund={fund}
            unitCategoryCode={unitCategoryCode}
            isMobile={isMobile}
            processLayout={false}
            getFundManagerPhotoPromise={(peopleIds) => getFundManagerPhoto(peopleIds)}
            className={onSelectFund ? c.fundDetailsDialogGrid : undefined}
          />
          </div>
            <AnimatePresence>
              {showBottomBar && <FundDetailsDialogBottomBar fullWidth={true}>
                <div className={c.bottomBar}>
                  <div>
                    {!isMobile && <FundListItemIcons
                        fundValue={undefined}
                        fullNameUmbrella={!isMobile}
                        fund={fund}
                        isFundInadequate={false}
                        umbrellaColor={toHashColorHexString(fund?.umbrella?.colorHex)}
                    />}
                    <Typography
                        variant={isMobile ? 'body1' : 'h3'}
                        component="h3"
                        className={c.bottomBarFundName}
                    >
                      {fund.name}
                  </Typography>
                  {(fund.previousName && !isMobile) && (
                    <Typography
                    component="p"
                    variant="body2"
                    className={c.bottomBarPreviousName}
                  >
                      {fund.previousName}
                    </Typography>
                  )}
                  </div>
                  <div>
                    {renderSelectButton()}
                  </div>
                </div>
              </FundDetailsDialogBottomBar>}
            </AnimatePresence>
          </DialogContent>
        </Dialog>
      </>
  );
};

export default FundDetailsButtonWithDialog;
